import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Row, Col, FormGroup, ButtonGroup, Button, Card, CardTitle, CardBody, CardFooter } from 'reactstrap';
import { SortableContainer, SortableElement, SortableHandle } from 'react-sortable-hoc';
import SelectField from '../../SelectField/SelectField';
import TranslatableField from '../../TranslatableField/TranslatableField';
import { change as changeFormValue } from 'redux-form';
import { changeSetting } from '../../../redux/modules/settings';
import { normalize_theme_logos } from '../ThemeSettings/ThemeSettings';
import { Utils } from '../../../services';
import { arrayMoveMutable } from 'array-move';
import i18n from '../../../locales/i18n';

import { ExploritNavIcon } from '../exploritNavIcon';
import NavGlyphPicker from './NavGlyphPicker';
import './NavigationSettings.css';

const NavigationSettings = (props) => {
    const { t }    = useTranslation();
    const dispatch = useDispatch();
    const { env } = props;

    const [chooserIsVisible, showChooser] = useState(false);
    const [glyphIndex, setGlyphIndex] = useState(null);

    if (!props.setting)
        return null;

    const tabs = props.setting.mobile?.[env]?.navigation?.tabs || [];
    const logos = normalize_theme_logos(props.setting.mobile?.[env]?.theme?.logo);
    const logoUri = logos.find((logo) => logo?.rfc2397_data)?.rfc2397_data || '';

    const tabLabel = (tab) => {
        const localizedName = Utils.localized_or_fallback(tab?.name, i18n.language);
        if (localizedName)
            return localizedName;

        const translatedTarget = t(tab?.target, { defaultValue: '' });
        if (translatedTarget && translatedTarget !== tab?.target)
            return translatedTarget;

        if (tab?.target === 'settings.edition.title' || tab?.target === 'settings.edition')
            return t('settings.edition.title');

        return tab?.target;
    };

    const reorderHint = t('common.drag_to_reorder');
    const DragHandle  = SortableHandle(() => <i className="drag-handle fa fa-bars" title={reorderHint} aria-label={reorderHint} />);
    const SortableTab = SortableElement(({ tab, sortIndex }) => (
        <Col sm={ 3 }>
            <Card className="tab mt-3">
                <DragHandle />
                <CardTitle>{ tabLabel(tab) }</CardTitle>
                <CardBody>
                    <button type="button" className="tab-icon-btn" onClick={ () => setGlyphIndex(sortIndex) }>
                        <ExploritNavIcon icon={ tab.icon } logoUri={ logoUri } />
                        <span>{ t('settings.mobile.navigation.choose_icon') }</span>
                    </button>
                    <TranslatableField component="input" type="text" name={ `mobile.${env}.navigation.tabs[${ sortIndex }].name` } />
                </CardBody>
                <CardFooter>
                    <ButtonGroup>
                        <Button color="danger" size="sm" onClick={ () => onRemove(sortIndex) }>
                            <i className="fa fa-trash"></i>&nbsp;{t("common.delete")}
                        </Button>
                    </ButtonGroup>
                </CardFooter>
            </Card>
        </Col>
    ));

    const SortableGrid = SortableContainer(({ tabs }) => {
        return (
            <Row>
                { (tabs || []).map((tab, index) => <SortableTab key={ `item-${ index }` } index={ index } tab={ tab } sortIndex={ index }/>)}
            </Row>
        );
    });

    const onSortEnd = ({oldIndex, newIndex}) => {
        const newSetting = { ...props.setting };
        arrayMoveMutable(newSetting.mobile?.[env]?.navigation?.tabs || [], oldIndex, newIndex);
        dispatch(changeSetting(newSetting));
    };

    const onRemove = (index) => {
        const newSetting = { ...props.setting };
        const newTabs = [].concat(newSetting.mobile?.[env]?.navigation?.tabs || []);
        newTabs.splice(index, 1);
        newSetting.mobile = (newSetting.mobile || {});
        newSetting.mobile[env] = (newSetting.mobile[env] || {});
        newSetting.mobile[env].navigation = (newSetting.mobile[env].navigation || {});
        newSetting.mobile[env].navigation.tabs = newTabs;
        dispatch(changeSetting(newSetting));
    }

    const onIcon = (index, icon) => {
        const newSetting = { ...props.setting };
        const newTabs = [].concat(newSetting.mobile?.[env]?.navigation?.tabs || []);
        newTabs[index] = { ...newTabs[index], icon };
        newSetting.mobile = (newSetting.mobile || {});
        newSetting.mobile[env] = (newSetting.mobile[env] || {});
        newSetting.mobile[env].navigation = (newSetting.mobile[env].navigation || {});
        newSetting.mobile[env].navigation.tabs = newTabs;
        // The phone preview reads redux-form values before the setting store.
        dispatch(changeFormValue(
            'settingForm',
            `mobile.${env}.navigation.tabs[${index}].icon`,
            icon
        ));
        dispatch(changeSetting(newSetting));
        setGlyphIndex(null);
    };

    const onAdd = (target) => {
        const newSetting = { ...props.setting };
        const newTabs = [].concat(newSetting.mobile?.[env]?.navigation?.tabs || []);
        const template = availableTabs.find(tab => tab.target === target);
        if (template) {
            newTabs.push({
                ...template,
                name: { ...template.name },
                icon: template.icon ? { ...template.icon } : template.icon,
            });
        }
        newSetting.mobile = (newSetting.mobile || {});
        newSetting.mobile[env] = (newSetting.mobile[env] || {});
        newSetting.mobile[env].navigation = (newSetting.mobile[env].navigation || {});
        newSetting.mobile[env].navigation.tabs = newTabs;
        newSetting.mobile[env].navigation.tabs = newTabs;
        dispatch(changeSetting(newSetting));
        showChooser(false);
    }

    const availableTabs = [
        { name: {fr: 'Accueil', en: 'Home', de: 'Home', it: 'Home'}, icon: { type: 'logo' }, target: 'Home' },
        { name: {fr: 'Billets', en: 'Tickets', de: 'Tickets', it: 'Biglietti'}, icon: { name: 'ticket', type: 'font-awesome' }, target: 'Ticket' },
        { name: {fr: 'Favoris', en: 'Favorites', de: 'Favoriten', it: 'Preferiti'}, icon: { name: 'heart', type: 'font-awesome' }, target: 'Favoris' },
        { name: {fr: 'Shop', en: 'Shop', de: 'Shop', it: 'Shop'}, icon: { name: 'shopping-basket', type: 'font-awesome' }, target: 'SteamShop' },
        { name: {fr: 'News', en: 'News', de: 'News', it: 'News'}, icon: { name: 'newspaper-o', type: 'font-awesome' }, target: 'News' },
        { name: {fr: 'Panier', en: 'Cart', de: 'Warenkorb', it: 'Carrello'}, icon: { name: 'shopping-cart', type: 'font-awesome' }, target: 'Panier' },
        { name: {fr: 'Programme', en: 'Program', de: 'Programm', it: 'Programma'}, icon: { name: 'calendar', type: 'font-awesome' }, target: 'Programme' },
        { name: {fr: 'Votes', en: 'Votes', de: 'Abstimmen', it: 'Voti'}, icon: { name: 'star', type: 'font-awesome' }, target: 'Votes' },
    ];

    return (
        <div className="NavigationSettings">
            <div className="small card-title d-flex align-items-center">
                { t('settings.mobile.navigation.tabs') }
                <Button className="ml-auto btn btn-success" onClick={ () => showChooser(true) }>
                    <i className="fa fa-plus"></i>&nbsp;
                    { t('settings.mobile.navigation.add_tab') }
                </Button>
            </div>
            <p className="desc">
                { t('settings.mobile.navigation.tabs_desc') }
            </p>
            <SortableGrid
                tabs={ tabs }
                axis="xy"
                onSortEnd={ onSortEnd }
                useDragHandle={ true }
                lockToContainerEdges={ true }
            />
                { tabs && tabs.length > 0 && (
            <>
                <div className="small card-title">
                    { t('settings.mobile.navigation.active_tab') }
                </div>
                <p className="desc">
                    { t('settings.mobile.navigation.active_tab_desc') }
                </p>
                <FormGroup row>
                    <Col>
                        <SelectField
                            name={`mobile.${env}.navigation.initialTab`}
                            options={ tabs.map(tab => ({
                                key: tab.target,
                                text: tabLabel(tab),
                                value: tab.target
                            }))}
                        />
                    </Col>
                </FormGroup>
            </>
            )}

            { glyphIndex != null && tabs[glyphIndex] && (
                <NavGlyphPicker
                    icon={ tabs[glyphIndex].icon }
                    logoUri={ logoUri }
                    onSelect={ (icon) => onIcon(glyphIndex, icon) }
                    onClose={ () => setGlyphIndex(null) }
                />
            )}

            { chooserIsVisible && (
            <div className="tab-chooser-backdrop">
                <div className="tab-chooser">
                    <Button className="close-btn" color="link" onClick={ () => showChooser(false)}>
                        <i className="fa fa-times" />
                    </Button>
                    <div className="small card-title">
                        { t('settings.mobile.navigation.choose_a_tab') }
                    </div>
                    <div className="tab-choices">
                        { availableTabs.map(tab => (
                        <div className="tab-choice" onClick={() => onAdd(tab.target)}>
                            <div className="tab-choice-title">
                                { tab.name[i18n.language] }
                            </div>
                        </div>
                        ))}
                    </div>
                </div>
            </div>
            )}
        </div>
    );
}

export default NavigationSettings;
